<template>
  <div
    class="d-flex flex-wrap justify-content-center align-items-end column-gap-2"
  >
    <LoadingIndicatorComponent v-if="isLoading" inline />
    <PendingPayoutAmount
      v-else-if="parts.length === 0"
      data-test="pending-payout-exact"
      :amount="null"
      :currency="DEFAULT_CURRENCY"
      :variant
    />
    <template v-else>
      <template v-for="(part, index) in parts" :key="part.currency">
        <span
          v-if="index > 0"
          data-test="pending-payout-separator"
          class="text-muted"
          aria-hidden="true"
          >·</span
        >
        <!-- Where part of the amount converts at today's rate the actual value may differ; the rate is on hover -->
        <InfoTooltip
          v-if="part.conversion !== null"
          :text="
            part.conversion.rate
              ? $t('requestInvoice.indicative_payout_explainer', {
                  rate: part.conversion.rate.rate,
                  date: part.conversion.rate.rate_date,
                })
              : $t('requestInvoice.indicative_payout_explainer_without_rate')
          "
        >
          <PendingPayoutAmount
            data-test="indicative-pending-payout"
            :amount="part.amount"
            :currency="part.currency"
            :variant
          />
        </InfoTooltip>
        <PendingPayoutAmount
          v-else
          data-test="pending-payout-exact"
          :amount="part.amount"
          :currency="part.currency"
          :variant
        />
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import InfoTooltip from "@/components/common/InfoTooltip.vue";
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import type { PendingPayoutPart } from "@/composables/money/invoiceUtils";
import { DEFAULT_CURRENCY } from "@/money/currency_constants";
import PendingPayoutAmount from "@/money/PendingPayoutAmount.vue";

/**
 * A "Money pending" figure, one amount per currency, never summed across currencies. An amount
 * partly converted at today's ECB rate carries the rate on hover, since the actual payout may
 * differ; an amount with nothing to convert shows as it is. No parts read "-".
 *
 * `stat` is the dashboard headline (number with its unit beside it); `summary` is the single bold
 * line of the Submit invoice box.
 */
withDefaults(
  defineProps<{
    parts: PendingPayoutPart[];
    isLoading?: boolean;
    variant?: "stat" | "summary";
  }>(),
  { variant: "stat" }
);
</script>
